import { useEffect, useState } from "react";

import {
  Button,
  ErrorHollowSVG,
  Icon,
  LoadingSpinner,
  RadioButton,
  visuallyHiddenStyles,
} from "@newsela/angelou";

import { useRouter } from "next/router";
import { actionPrefixes, actionNames, withTrackEvent } from "@newsela/snitch";
import {
  handleRemoveClassroomAssignee,
  handleRemoveStudentAssignee,
  handleUpdateClassroomAssignees,
  handleUpdateStudentAssignees,
} from "@/assignments/utils/assignmentActions";
import { Classroom } from "@/types/models/Classroom.interface";
import {
  AssignmentGroup,
  Classroom as AssignmentClassroom,
  ClassroomStudent,
} from "@/types/models/Assignment.interface";
import { type Assignment } from "@/types/models/Assignment.interface";
import useClassroomsContext from "@/classrooms/useClassroomsContext";

import useUserExperimentSegments from "@/users/useUserExperimentSegments";
import AssignSetupClassesWarning from "../AssignSetupClassesWarning/AssignSetupClassesWarning";
import WholeClassAssignmentOptions from "./WholeClassAssignmentOptions";
import SmallGroupAssignmentOptions from "./SmallGroupAssignmentOptions";
import {
  asteriskCss,
  errorMessageCss,
  errorMessageIconCss,
  errorMessageWrapper,
  fieldsetCss,
  optionCss,
  optionsWrapperCss,
  wrapperCss,
} from "./AssignmentOptions.style";

export interface AssignmentOption {
  label: string;
  value: string;
}

const ButtonWithTrackEvent = withTrackEvent(Button);
const RadioButtonWithTrackEvent = withTrackEvent(RadioButton);

export interface AssignmentOptionsInterface {
  assignment: {
    assignment_classrooms: AssignmentClassroom[];
    assignment_groups: AssignmentGroup[];
  };
  contentId: string;
  dispatch: () => void;
  hasError: boolean;
  dismissAssigneeError: () => void;
  handleSaveDraft: () => Promise<{ data: Assignment }>;
}

const AssignmentOptionsFieldSetWrapper = ({
  children,
  hasError,
  errorMessage,
}: {
  children: React.ReactElement;
  hasError: boolean;
  errorMessage: React.ReactElement;
}) => (
  <fieldset css={fieldsetCss}>
    <legend css={visuallyHiddenStyles}>
      Choose whole class or small group assignment
    </legend>
    <h2>
      Students <span css={asteriskCss(hasError)}>*</span>
    </h2>
    {hasError ? <div css={errorMessageWrapper}>{errorMessage}</div> : null}
    {children}
  </fieldset>
);

export const AssignmentOptions = ({
  assignment,
  contentId,
  dispatch,
  hasError,
  dismissAssigneeError,
  handleSaveDraft,
}: AssignmentOptionsInterface) => {
  const router = useRouter();
  const INITIAL_VISIBLE_CLASSROOMS = 3;
  const [selectedOption, setSelectedOption] = useState<AssignmentOption | null>(
    null,
  );
  const {
    classrooms: allTeacherClassrooms = [],
    loading: isClassroomsLoading,
  } = useClassroomsContext() as {
    classrooms: Classroom[] | undefined;
    loading: boolean;
    error: Error | undefined;
  };
  const [areAllClassroomsVisible, setAreAllClassroomsVisible] = useState(
    !isClassroomsLoading &&
      allTeacherClassrooms.length <= INITIAL_VISIBLE_CLASSROOMS,
  );
  const { assignment_classrooms, assignment_groups } = assignment || {};

  const assignmentOptions: AssignmentOption[] = [
    {
      label: "Whole class",
      value: "wholeClass",
    },
    {
      label: "Small group",
      value: "smallGroup",
    },
  ];

  const noSelection =
    assignment_classrooms?.length === 0 && assignment_groups?.length === 0;
  const [wholeClassOption, smallGroupOption] = assignmentOptions;

  useEffect(() => {
    if (noSelection || assignment_groups?.length === 0) {
      setSelectedOption(wholeClassOption || null);
    } else {
      setSelectedOption(smallGroupOption || null);
    }
  }, []);

  const handleOptionChange = (option: AssignmentOption) => {
    setSelectedOption(option);
  };

  const handleUpdateClassrooms = (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) => {
    const selectedClassroom = (e.target as HTMLInputElement).value;
    const selectedClassroomObject = allTeacherClassrooms?.find(
      (classroom) => classroom.unique_id === selectedClassroom,
    );

    const isChecked = assignment_classrooms.find(
      (classroom: AssignmentClassroom) =>
        classroom.unique_id === selectedClassroom,
    );

    if (isChecked) {
      handleRemoveClassroomAssignee(isChecked, dispatch);
    } else {
      handleUpdateClassroomAssignees(
        [selectedClassroomObject, ...assignment_classrooms],
        dispatch,
      );
    }
  };

  const handleUpdateStudents = (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) => {
    const selectedStudentUniqueId = (e.target as HTMLInputElement).value;

    const [group] = assignment_groups;
    const users = group?.users || [];

    const selectedStudentClassroomArray: Classroom[] =
      allTeacherClassrooms?.filter((classroom) =>
        classroom.students.find(
          (student) => student.unique_id === selectedStudentUniqueId,
        ),
      ) ?? [];

    const [foundClassroom] = selectedStudentClassroomArray;

    const selectedStudentObject = foundClassroom?.students.find(
      (student: ClassroomStudent) =>
        student.unique_id === selectedStudentUniqueId,
    );

    const foundStudent = users.find(
      (user) => user.unique_id === selectedStudentObject?.unique_id,
    );

    const isAlreadyAssigned = foundStudent !== undefined;

    if (!isAlreadyAssigned) {
      handleUpdateStudentAssignees([selectedStudentObject, ...users], dispatch);
    } else {
      handleRemoveStudentAssignee(selectedStudentObject, dispatch);
    }
  };

  const handleClickOnGoToClassDirectory = async () => {
    const savedAssignmentResponse = await handleSaveDraft();
    const path = router.asPath.split("?").at(0);
    const { contentId, ...restParams } = router.query;
    const urlSearchParams = new URLSearchParams({
      ...restParams,
      selectedAssignmentId: savedAssignmentResponse.data.content_id,
    });
    void router.push({
      pathname: "/account/classes",
      query: {
        cameFromAssignmentPage: true,
        next: `${path}?${encodeURI(urlSearchParams.toString())}`,
      },
    });
  };

  const errorMessage = (
    <div css={errorMessageCss}>
      <Icon
        SvgComponent={ErrorHollowSVG}
        __cssFor={{
          root: errorMessageIconCss,
        }}
      />
      <div>Please add at least one assignee</div>
    </div>
  );

  const { data: experiments } = useUserExperimentSegments();

  const shouldShowClassroomsWarning =
    experiments?.["class_setup_assignment_builder"] === "on" &&
    !allTeacherClassrooms?.length;
  const seeAllClassroomsText = "See all classrooms";
  const seeFewerClassroomsText = "See fewer classrooms";

  if (isClassroomsLoading) {
    return (
      <AssignmentOptionsFieldSetWrapper
        hasError={hasError}
        errorMessage={errorMessage}
      >
        <LoadingSpinner />
      </AssignmentOptionsFieldSetWrapper>
    );
  }

  if (shouldShowClassroomsWarning) {
    return (
      <AssignmentOptionsFieldSetWrapper
        hasError={hasError}
        errorMessage={errorMessage}
      >
        <AssignSetupClassesWarning
          onClickGoToClassDirectory={handleClickOnGoToClassDirectory}
        />
      </AssignmentOptionsFieldSetWrapper>
    );
  }

  return (
    <div css={wrapperCss}>
      <AssignmentOptionsFieldSetWrapper
        hasError={hasError}
        errorMessage={errorMessage}
      >
        <div css={optionsWrapperCss} role="radiogroup">
          {assignmentOptions.map((option) => {
            return (
              <RadioButtonWithTrackEvent
                actionName={actionNames.create}
                actionPrefix={actionPrefixes.initiate}
                checked={selectedOption?.value === option.value}
                dataProps={{
                  "data-qa-selector": `assignment-option-${option.value}`,
                }}
                key={option.value}
                label={option.label}
                name="assignmentOption"
                objectType="Assignment"
                onChange={() => handleOptionChange(option)}
                streamProperties={{
                  content_id: contentId,
                  content_type: "Assignment",
                  description: `Assign to ${option.label}`,
                }}
                value={option.value as unknown as string} // Ignore the error by casting to string
                __cssFor={{
                  root: optionCss,
                }}
              />
            );
          })}
        </div>
      </AssignmentOptionsFieldSetWrapper>
      {selectedOption?.value === wholeClassOption?.value ? (
        <WholeClassAssignmentOptions
          allTeacherClassrooms={allTeacherClassrooms}
          areAllClassroomsVisible={areAllClassroomsVisible}
          assignment={assignment}
          hasError={hasError}
          onClick={(e) => {
            dismissAssigneeError();
            handleUpdateClassrooms(e);
          }}
        />
      ) : (
        <SmallGroupAssignmentOptions
          allTeacherClassrooms={allTeacherClassrooms}
          areAllClassroomsVisible={areAllClassroomsVisible}
          assignment={assignment}
          hasError={hasError}
          onClick={(e) => {
            dismissAssigneeError();
            handleUpdateStudents(e);
          }}
        />
      )}
      {allTeacherClassrooms?.length > INITIAL_VISIBLE_CLASSROOMS ? (
        <ButtonWithTrackEvent
          actionName={actionNames.add}
          actionPrefix={actionPrefixes.initiate}
          dataProps={{
            "data-qa-selector": `${
              areAllClassroomsVisible
                ? seeFewerClassroomsText
                : seeAllClassroomsText
            } button`,
          }}
          flavor={Button.flavors.tertiary}
          objectType="Assignment"
          onClick={() => {
            setAreAllClassroomsVisible((prevState) => !prevState);
          }}
          streamProperties={{
            description: `Toggle ${
              areAllClassroomsVisible
                ? seeFewerClassroomsText
                : seeAllClassroomsText
            } button`,
          }}
        >
          {areAllClassroomsVisible
            ? seeFewerClassroomsText
            : seeAllClassroomsText}
        </ButtonWithTrackEvent>
      ) : null}
    </div>
  );
};

export default AssignmentOptions;
